/**
 * THE APPEAL AS CLOUDS: every marketing angle, persuasion tactic, audience and role the
 * room's companies use, sized by how many companies use it. A tag nobody uses is still
 * drawn, small and grey, because the gaps are half of what this is for. Clicking a tag
 * opens the companies behind it underneath: the pages that carry it, quoted, and whether
 * the company leads with it. The room's own ideas are marked where they would lead.
 *
 * The rows are the same ones the table shows (`AppealReport`); nothing is fetched here.
 */
import { useMemo, useState } from 'react';
import type { BoardroomAppeal, BoardroomAppealPage, BoardroomAppealRow } from '../../../types';
import { wordSize } from './FeatureCloudReport';

type Row = BoardroomAppealRow;
type Idea = NonNullable<BoardroomAppeal['ideas']>[number];

export type TagKind = 'angle' | 'tactic' | 'audience' | 'role';
export interface TagPick {
  kind: TagKind;
  value: string;
}
export interface TagMatch {
  row: Row;
  /** For an angle: one the company leads with, not only one a page uses. */
  lead: boolean;
  /** The pages that carry it (angles and tactics only). */
  pages: BoardroomAppealPage[];
}

/** A shared id in words: `avoid_wasted_spend` is "avoid wasted spend". Pure. */
export function words(id: string): string {
  return id.replace(/_/g, ' ');
}

/** The companies behind one tag, the ones that lead with it first, then by how many of
 *  their pages carry it. Pure. */
export function matchesFor(rows: Row[], pick: TagPick): TagMatch[] {
  const out: TagMatch[] = [];
  for (const row of rows) {
    if (pick.kind === 'angle' || pick.kind === 'tactic') {
      const onPage = (p: BoardroomAppealPage) =>
        (pick.kind === 'angle' ? (p.angles ?? []) : (p.persuasion ?? [])).includes(pick.value);
      const pages = (row.pages ?? []).filter(onPage);
      const own = (pick.kind === 'angle' ? row.angles : row.persuasion) ?? [];
      const lead = pick.kind === 'angle' && own.includes(pick.value);
      if (pages.length || own.includes(pick.value)) out.push({ row, lead, pages });
    } else {
      const own = (pick.kind === 'audience' ? row.audiences : row.roles) ?? [];
      if (own.includes(pick.value)) out.push({ row, lead: false, pages: [] });
    }
  }
  return out.sort(
    (a, b) =>
      Number(b.lead) - Number(a.lead) || b.pages.length - a.pages.length || a.row.title.localeCompare(b.row.title),
  );
}

/** How many companies use each value of one kind of tag. Pure. */
export function countsFor(rows: Row[], kind: TagKind, values: string[]): Map<string, number> {
  return new Map<string, number>(values.map(v => [v, matchesFor(rows, { kind, value: v }).length]));
}

/** The open values (audiences, roles) the rows use, most used first. Pure. */
export function openValues(rows: Row[], kind: 'audience' | 'role'): string[] {
  const seen = new Map<string, number>();
  for (const r of rows) {
    for (const v of (kind === 'audience' ? r.audiences : r.roles) ?? []) seen.set(v, (seen.get(v) ?? 0) + 1);
  }
  return Array.from(seen.entries())
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
    .map(([v]) => v);
}

function shortPath(url: string): string {
  const path = url.replace(/^https?:\/\/[^/]+/, '').replace(/^\/|\/$/g, '');
  return path || 'home';
}

const MUTED = 'text-gray-500 dark:text-gray-400';
const LINK = 'text-indigo-600 dark:text-indigo-400 hover:underline text-left';

function Word({
  value,
  count,
  most,
  title,
  selected,
  ours,
  onPick,
}: {
  value: string;
  count: number;
  most: number;
  title?: string;
  selected: boolean;
  ours: boolean;
  onPick: () => void;
}) {
  const tone =
    count === 0
      ? 'text-gray-300 italic dark:text-gray-600'
      : count / Math.max(1, most) >= 0.5
        ? 'text-indigo-800 font-semibold dark:text-indigo-200'
        : 'text-indigo-600 dark:text-indigo-300';
  return (
    <button
      type="button"
      onClick={onPick}
      title={title}
      style={{ fontSize: `${count ? wordSize(count, most) : 11}px` }}
      className={`rounded px-0.5 hover:underline ${tone} ${selected ? 'bg-indigo-100 ring-1 ring-indigo-300 dark:bg-indigo-900/50' : ''}`}
      aria-pressed={selected}
      data-testid={`appeal-tag-${value}`}
    >
      {words(value)}
      <span className="ml-0.5 align-super text-[11px] font-normal text-gray-400 dark:text-gray-500">{count}</span>
      {ours && (
        <span
          className="ml-0.5 align-super text-[10px] font-normal text-emerald-600 dark:text-emerald-400"
          title="One of your ideas would lead with this"
        >
          yours
        </span>
      )}
    </button>
  );
}

function Matches({
  pick,
  matches,
  meaning,
  ideas,
  onOpen,
  onClose,
}: {
  pick: TagPick;
  matches: TagMatch[];
  meaning?: string;
  ideas: Idea[];
  onOpen: (id: number) => void;
  onClose: () => void;
}) {
  return (
    <div
      className="my-2 rounded border border-indigo-200 bg-indigo-50/40 p-2 dark:border-indigo-800 dark:bg-indigo-900/10"
      data-testid="appeal-matches"
    >
      <div className="flex items-baseline gap-2">
        <span className="font-medium text-gray-800 dark:text-gray-100">{words(pick.value)}</span>
        <span className={MUTED}>
          {matches.length} compan{matches.length === 1 ? 'y' : 'ies'}
          {meaning ? ` · ${meaning}` : ''}
        </span>
        <button type="button" onClick={onClose} className={`ml-auto ${MUTED} hover:underline`} aria-label="Close">
          close
        </button>
      </div>
      {ideas.length > 0 && (
        <p className="mt-1 text-emerald-700 dark:text-emerald-400">
          Your ideas that lead with it:{' '}
          {ideas.map((i, n) => (
            <span key={i.id}>
              {n > 0 && ', '}
              <button type="button" onClick={() => onOpen(i.id)} className="hover:underline">
                {i.title}
              </button>
            </span>
          ))}
        </p>
      )}
      {matches.length === 0 && (
        <p className={`mt-1 ${MUTED}`}>No company read so far uses this. That may be an opening.</p>
      )}
      <ul className="mt-1 space-y-1.5">
        {matches.map(m => (
          <li key={m.row.id}>
            <button type="button" onClick={() => onOpen(m.row.id)} className={`${LINK} font-medium`}>
              {m.row.title}
            </button>
            {m.lead && (
              <span className="ml-1 rounded bg-indigo-100 px-1 text-[11px] text-indigo-700 dark:bg-indigo-900/50 dark:text-indigo-300">
                leads with it
              </span>
            )}
            {m.pages.length > 0 ? (
              <ul className="ml-3 mt-0.5 space-y-0.5">
                {m.pages.map(p => (
                  <li key={p.url}>
                    <a href={p.url} target="_blank" rel="noopener noreferrer" className={LINK}>
                      {shortPath(p.url)}
                    </a>
                    {p.intent && <span className={MUTED}> ({p.intent})</span>}
                    {p.message && <span className="text-gray-700 dark:text-gray-300"> &ldquo;{p.message}&rdquo;</span>}
                  </li>
                ))}
              </ul>
            ) : (
              (m.row.audience || m.row.promise) && (
                <div className={`ml-3 ${MUTED}`}>{[m.row.audience, m.row.promise].filter(Boolean).join(' · ')}</div>
              )
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function AppealCloud({ data, onOpen }: { data: BoardroomAppeal; onOpen: (itemId: number) => void }) {
  const [pick, setPick] = useState<TagPick | null>(null);
  const rows = data.rows;
  const ideas = data.ideas ?? [];
  const families = data.taxonomy?.families ?? [];
  const tactics = data.taxonomy?.tactics ?? [];
  const audiences = useMemo(() => openValues(rows, 'audience'), [rows]);
  const roles = useMemo(() => openValues(rows, 'role'), [rows]);
  const angleCounts = useMemo(
    () =>
      countsFor(
        rows,
        'angle',
        families.flatMap(f => f.angles.map(a => a.angle)),
      ),
    [rows, families],
  );
  const tacticCounts = useMemo(
    () =>
      countsFor(
        rows,
        'tactic',
        tactics.map(t => t.tactic),
      ),
    [rows, tactics],
  );
  const audienceCounts = useMemo(() => countsFor(rows, 'audience', audiences), [rows, audiences]);
  const roleCounts = useMemo(() => countsFor(rows, 'role', roles), [rows, roles]);
  const ourAngles = new Set(ideas.flatMap(i => i.angles ?? []));
  const meanings = new Map<string, string>([
    ...families.flatMap(f => f.angles.map(a => [a.angle, a.meaning] as [string, string])),
    ...tactics.map(t => [t.tactic, t.meaning] as [string, string]),
  ]);

  const toggle = (next: TagPick) =>
    setPick(cur => (cur && cur.kind === next.kind && cur.value === next.value ? null : next));
  const isPicked = (kind: TagKind, value: string) => !!pick && pick.kind === kind && pick.value === value;
  const panel = (kind: TagKind) =>
    pick?.kind === kind && (
      <Matches
        pick={pick}
        matches={matchesFor(rows, pick)}
        meaning={meanings.get(pick.value)}
        ideas={kind === 'angle' ? ideas.filter(i => (i.angles ?? []).includes(pick.value)) : []}
        onOpen={onOpen}
        onClose={() => setPick(null)}
      />
    );
  const cloud = (kind: TagKind, values: string[], counts: Map<string, number>) => {
    const most = Math.max(1, ...Array.from(counts.values()));
    return (
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 leading-tight">
        {values.map(v => (
          <Word
            key={v}
            value={v}
            count={counts.get(v) ?? 0}
            most={most}
            title={meanings.get(v)}
            selected={isPicked(kind, v)}
            ours={kind === 'angle' && ourAngles.has(v)}
            onPick={() => toggle({ kind, value: v })}
          />
        ))}
      </div>
    );
  };
  const heading = (text: string, note?: string) => (
    <div className="mt-4 mb-2 border-t border-gray-200 pt-3 first:mt-0 first:border-t-0 first:pt-0 dark:border-gray-700">
      <span className="text-sm font-semibold uppercase tracking-wide text-gray-800 dark:text-gray-100">{text}</span>
      {note && <span className={`ml-2 text-[11px] ${MUTED}`}>{note}</span>}
    </div>
  );

  if (!rows.some(r => r.state === 'read')) {
    return <p className={MUTED}>No company has been read yet. Press Read appeal to fill this in.</p>;
  }
  // The angles, a family at a time, biggest family first; the largest count across all
  // angles sets the size, so families can be compared with each other.
  const allMost = Math.max(1, ...Array.from(angleCounts.values()));
  const familyUse = (angles: { angle: string }[]) => angles.reduce((n, a) => n + (angleCounts.get(a.angle) ?? 0), 0);
  const orderedFamilies = families.slice().sort((a, b) => familyUse(b.angles) - familyUse(a.angles));
  return (
    <div data-testid="appeal-cloud">
      {heading('Marketing angles', 'bigger means more companies use it · gray means none do · click one to see who')}
      <div className="space-y-1.5">
        {orderedFamilies.map(f => (
          <div key={f.family} className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
            <span className="w-40 shrink-0 text-xs font-medium text-gray-700 dark:text-gray-300">
              {words(f.family)}
              {f.frame === 'avoid' && ' (avoid)'}
            </span>
            {f.angles.map(a => (
              <Word
                key={a.angle}
                value={a.angle}
                count={angleCounts.get(a.angle) ?? 0}
                most={allMost}
                title={a.meaning}
                selected={isPicked('angle', a.angle)}
                ours={ourAngles.has(a.angle)}
                onPick={() => toggle({ kind: 'angle', value: a.angle })}
              />
            ))}
          </div>
        ))}
      </div>
      {panel('angle')}
      {heading('Persuasion', 'what the pages show to be believed')}
      {cloud(
        'tactic',
        tactics.map(t => t.tactic),
        tacticCounts,
      )}
      {panel('tactic')}
      {audiences.length > 0 && (
        <>
          {heading('Who they sell to')}
          {cloud('audience', audiences, audienceCounts)}
          {panel('audience')}
        </>
      )}
      {roles.length > 0 && (
        <>
          {heading('Who the pages speak to')}
          {cloud('role', roles, roleCounts)}
          {panel('role')}
        </>
      )}
    </div>
  );
}
